import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { api } from "@/lib/api";
import { Trophy, Flame, Snowflake, Star, Target, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function WeeklyRecap() {
  const [r, setR] = useState(null);
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => { api.weeklyRecap().then(setR).catch(() => {}); }, []);
  if (!r) return null;

  const share = async () => {
    setSharing(true);
    try {
      const dataUrl = await toPng(shareRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#4A5D23" });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "calorify-week.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Calorify week", text: r.cheer });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "calorify-week.png";
        a.click();
        toast.success("Recap image saved to your device!");
      }
    } catch (e) {
      toast.error("Couldn't create the image. Try again.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl overflow-hidden border border-border bg-card"
        data-testid="weekly-recap"
      >
        <div className="p-5 bg-gradient-to-br from-primary to-[#3a4a1c] text-primary-foreground">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] opacity-80">
              <Trophy className="w-4 h-4" /> {r.is_sunday ? "Happy Sunday · Recap" : "This Week's Recap"}
            </div>
            <button
              onClick={share}
              disabled={sharing}
              data-testid="recap-share-btn"
              className="flex items-center gap-1.5 text-xs font-bold bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full transition-colors"
            >
              {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} Share
            </button>
          </div>
          <p className="mt-2 text-[15px] leading-relaxed font-medium" data-testid="recap-cheer">{r.cheer}</p>
        </div>
        <div className="grid grid-cols-3 divide-x divide-border">
          <RecapStat icon={Target} label="Days on goal" value={`${r.days_within_goal}/7`} />
          <RecapStat icon={Flame} label="Streak" value={`${r.streak}d`} />
          <RecapStat icon={Snowflake} label="Freezes" value={r.freezes} />
        </div>
        {r.best_day && (
          <div className="px-5 py-4 border-t border-border flex items-center gap-3 bg-secondary/30">
            <div className="w-9 h-9 rounded-xl bg-macro-carbs/15 flex items-center justify-center">
              <Star className="w-4.5 h-4.5" style={{ color: "#E69C24" }} />
            </div>
            <div className="flex-1">
              <div className="text-xs text-muted-foreground">Best day</div>
              <div className="font-semibold text-sm" data-testid="recap-best-day">{r.best_day.day}</div>
            </div>
            <div className="text-right">
              <div className="font-display font-medium tabular-nums text-sm">{r.best_day.calories} kcal</div>
              <div className="text-xs text-muted-foreground">{r.best_day.protein}g protein</div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Off-screen branded share card */}
      <div style={{ position: "fixed", left: -9999, top: 0, pointerEvents: "none" }} aria-hidden>
        <ShareCard innerRef={shareRef} r={r} />
      </div>
    </>
  );
}

const ShareCard = ({ r, innerRef }) => (
  <div
    ref={innerRef}
    style={{
      width: 540, padding: 48, boxSizing: "border-box",
      background: "linear-gradient(150deg, #55692a 0%, #4A5D23 45%, #2f3d17 100%)",
      color: "#F9F8F6", fontFamily: "Manrope, sans-serif",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: 0.85, fontSize: 15, letterSpacing: 3, textTransform: "uppercase" }}>
      ✦ calorify
    </div>
    <div style={{ fontFamily: "'Cabinet Grotesk', Manrope, sans-serif", fontSize: 46, fontWeight: 700, marginTop: 18, lineHeight: 1.05 }}>
      My week in food
    </div>
    <div style={{ fontSize: 17, lineHeight: 1.5, marginTop: 14, opacity: 0.92 }}>{r.cheer}</div>

    <div style={{ display: "flex", gap: 14, marginTop: 34 }}>
      <ShareStat value={`${r.days_within_goal}/7`} label="Days on goal" />
      <ShareStat value={`${r.streak}`} label="Day streak" />
      <ShareStat value={`${r.freezes}`} label="Freezes ❄" />
    </div>

    {r.best_day && (
      <div style={{ marginTop: 20, background: "rgba(255,255,255,0.12)", borderRadius: 22, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 12, opacity: 0.75, textTransform: "uppercase", letterSpacing: 1 }}>★ Best day</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>{r.best_day.day}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{r.best_day.calories} kcal</div>
          <div style={{ fontSize: 13, opacity: 0.8 }}>{r.best_day.protein}g protein</div>
        </div>
      </div>
    )}

    <div style={{ marginTop: 34, fontSize: 14, opacity: 0.7 }}>Understand your food. Reach your goals.</div>
  </div>
);

function ShareStat({ value, label }) {
  return (
    <div style={{ flex: 1, background: "rgba(255,255,255,0.1)", borderRadius: 20, padding: "20px 12px", textAlign: "center" }}>
      <div style={{ fontFamily: "'Cabinet Grotesk', Manrope, sans-serif", fontSize: 34, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function RecapStat({ icon: Icon, label, value }) {
  return (
    <div className="p-4 text-center">
      <Icon className="w-4 h-4 mx-auto text-primary mb-1.5" />
      <div className="text-lg font-display font-medium tabular-nums">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}
